import { cleanDoc } from '@/helpers/formatters';
import { validateCelular, validateCep, validateCpf, validateEmail, validateUf } from '@/helpers/validators';
import {
  CIVIL_STATUS_OPTIONS,
  type CivilStatus,
  type CustomerProfileForm,
  type PixKeyType,
} from '@/features/credito/types/customerProfile';
import { birthDateToIso } from '@/helpers/dates';
import { isKnownNationality, isOccupationValid } from '@/features/credito/domain/profileCatalog';

const ALLOWED_CIVIL_STATUS = new Set<string>(CIVIL_STATUS_OPTIONS.map(o => o.value));

export function isCivilStatusValid(value: string): value is CivilStatus {
  return ALLOWED_CIVIL_STATUS.has(value);
}

export function isNationalityValid(value: string): boolean {
  return isKnownNationality(value);
}

export function isPersonalDataStepValid(form: CustomerProfileForm): boolean {
  return (
    Boolean(form.name.trim()) &&
    birthDateToIso(form.birthDate) !== null &&
    validateCelular(form.phone) &&
    validateEmail(form.email)
  );
}

export function isContractDataStepValid(form: CustomerProfileForm): boolean {
  return (
    isCivilStatusValid(form.civilStatus) && isNationalityValid(form.nationality) && isOccupationValid(form.occupation)
  );
}

export function isAddressStepValid(form: CustomerProfileForm): boolean {
  return (
    validateCep(form.zipCode) &&
    Boolean(form.addressName.trim()) &&
    Boolean(form.district.trim()) &&
    Boolean(form.city.trim()) &&
    validateUf(form.uf)
  );
}

export function isPixStepValid(form: CustomerProfileForm): boolean {
  return isPixKeyValid(form.pixKeyType, form.pixKey);
}

export function isPixKeyValid(type: PixKeyType, key: string): boolean {
  const trimmed = key.trim();
  if (!trimmed) {
    return false;
  }
  switch (type) {
    case 'NaturalRegistrationNumber':
      return validateCpf(trimmed);
    case 'Phone':
      return validateCelular(trimmed);
    case 'Email':
      return validateEmail(trimmed);
    case 'Random':
      return trimmed.length >= 8;
    default:
      return false;
  }
}

export function pixKeyDigitsForApi(type: PixKeyType, key: string): string {
  if (type === 'NaturalRegistrationNumber' || type === 'Phone') {
    return cleanDoc(key);
  }
  return key.trim();
}
